import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// import { useDispatch } from "react-redux";

import images from "../../images";
import { OTPInput } from "../../components/OtpInputProps/OTPInputProps";
import {
  useVerifyOTPMutation,
  useLoginMutation,
  useVerifyOTPSignUpMutation,
  useResendOTPSignUpMutation,
  //   useGetUserInfoQuery,
} from "../../services/apiSlice";
import { toast } from "react-toastify";
// import {
//   setAccessToken,
//   setRefreshToken,
// } from "../../redux/featuresSlice/commonSlice";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { usePostHog } from "../../hooks/usePostHog";

export default function WhatsappOTPPage() {
  const navigate = useNavigate();
  // const dispatch = useDispatch();
  const { trackEvent, trackTimer } = usePostHog();

  const [otp, setOtp] = useState(Array(6).fill(""));
  const [searchParams] = useSearchParams();

  const phone = searchParams.get("phone");
  const countryCode = searchParams.get("country_code")?.replace(" ", "+") || "";
  const typeVerifyOTP = searchParams.get("type");
  const questId = searchParams.get("quest_id");
  const locationId = searchParams.get("location_id");
  const rememberMe = searchParams.get("remember_me");

  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifyOTP] = useVerifyOTPMutation();
  const [verifyOTPSignUp] = useVerifyOTPSignUpMutation();
  const [login] = useLoginMutation();
  const [resendOTPSignUp] = useResendOTPSignUpMutation();
  //   const { data: userInfo } = useGetUserInfoQuery();

  const { t } = useTranslation();

  const [failedAttempts, setFailedAttempts] = useState(0);
  const [blockTime, setBlockTime] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  
  // Track page load and potential abandonment
  useEffect(() => {
    const pageLoadTime = Date.now();

    // Track page abandonment
    const handleBeforeUnload = (event) => {
      trackEvent('otp_page_abandoned', {
        phone_number: phone,
        country_code: countryCode,
        signup_type: typeVerifyOTP,
        time_on_page_seconds: Math.round((Date.now() - pageLoadTime) / 1000),
        abandonment_reason: 'page_close_or_refresh',
        quest_context: questId ? 'from_quest' : 'direct_signup'
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackEvent('otp_page_backgrounded', {
          phone_number: phone,
          time_on_page_seconds: Math.round((Date.now() - pageLoadTime) / 1000),
          signup_type: typeVerifyOTP
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [trackEvent, phone, countryCode, typeVerifyOTP, questId, locationId]);

  // Track page view on component mount
  useEffect(() => {
    trackEvent('otp_verification_page_viewed', {
      verification_type: typeVerifyOTP,
      phone_country_code: countryCode,
      has_quest_context: !!questId,
      has_location_context: !!locationId
    });
  }, [trackEvent, typeVerifyOTP, countryCode, questId, locationId]);

  const handleSignIn = async () => {
    if (isBlocked) {
      toast.error(t("whatAppOTP.blockedError"));
      trackEvent('otp_verification_blocked', {
        verification_type: typeVerifyOTP,
        failed_attempts: failedAttempts
      });
      return;
    }

    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      toast.error(t("whatAppOTP.incompleteOTP"));
      trackEvent('otp_verification_incomplete', {
        verification_type: typeVerifyOTP,
        otp_length: otpValue.length
      });
      return;
    }

    // Track verification attempt
    const verificationTimer = trackTimer('otp_verification_duration');
    trackEvent('otp_verification_started', {
      verification_type: typeVerifyOTP,
      phone_country_code: countryCode,
      failed_attempts: failedAttempts
    });

    const body = {
      phone: phone,
      country_code: countryCode,
      otp_code: otp.join(""),
    };

    const handleVerifyOTP = async (apiCall) => {
      try {
        const response = await apiCall(body).unwrap();
        if (response.data.access_token && response.data.refresh_token) {
          if (rememberMe === "true") {
            localStorage.setItem("accessToken", response.data.access_token);
            localStorage.setItem("refreshToken", response.data.refresh_token);
          } else {
            sessionStorage.setItem("accessToken", response.data.access_token);
            sessionStorage.setItem("refreshToken", response.data.refresh_token);
          }
        }

        if (
          response.message === "Operation successful" ||
          response.status === 200
        ) {
          verificationTimer.end({
            success: true,
            verification_type: typeVerifyOTP
          });

          trackEvent('otp_verification_success', {
            verification_type: typeVerifyOTP,
            phone_country_code: countryCode,
            failed_attempts: failedAttempts,
            user_id: response.data?.id || 'unknown',
            next_destination: typeVerifyOTP === "signup" ? 'onboarding' : 
                            questId ? 'quest_details' : 
                            locationId ? 'quest_location' : 'home'
          });

          // Track signup completion and funnel step
          if (typeVerifyOTP === "signup") {
            trackEvent('signup_completed', {
              user_id: response.data?.id || 'unknown',
              phone_number: phone,
              country_code: countryCode,
              signup_method: 'whatsapp_otp',
              signup_source: questId ? 'quest_signup' : 'direct_signup',
              quest_id: questId || null,
              location_id: locationId || null,
              funnel_step: 'otp_verified',
              next_step: 'onboarding_preview'
            });
          }

          //   localStorage.setItem("userInfo", JSON.stringify(userInfo));

          // toast.success(t("toast.success.verifyOTPSuccess"));
          if (typeVerifyOTP === "signup") {
            navigate(`/preview?quest_id=${questId}&location_id=${locationId}`);
          } else if (questId !== ("null" || null)) {
            navigate(`/quest/details/${questId}`);
          } else if (locationId !== ("null" || null)) {
            navigate(`/quest/location/${locationId}`);
          } else {
            navigate("/");
          }
        }
      } catch (err) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        verificationTimer.end({
          success: false,
          verification_type: typeVerifyOTP,
          error_code: err.status,
          failed_attempts: newFailedAttempts
        });

        trackEvent('otp_verification_failed', {
          verification_type: typeVerifyOTP,
          phone_country_code: countryCode,
          error_code: err.status,
          failed_attempts: newFailedAttempts,
          is_blocked: newFailedAttempts >= 5,
          error_message: err.data?.detail?.[0]?.msg || err.message
        });

        if (newFailedAttempts >= 5) {
          setIsBlocked(true);
          setBlockTime(30);
          toast.error(t("whatAppOTP.blockedMessage"));
          setOtp(Array(6).fill(""));
          
          trackEvent('otp_verification_account_blocked', {
            verification_type: typeVerifyOTP,
            phone_country_code: countryCode,
            total_failed_attempts: newFailedAttempts
          });
        } else {
          toast.error(
            t("toast.error.verifyOTPFailed", {
              attempts: 5 - newFailedAttempts,
            })
          );
        }
      }
    };

    await handleVerifyOTP(
      typeVerifyOTP === "signup" ? verifyOTPSignUp : verifyOTP
    );
    posthog.reset();
    const userInfoStr = localStorage.getItem("userInfo");
    if (!userInfoStr) return;

    try {
      const userInfo = JSON.parse(userInfoStr);
      const userId = userInfo?.data?.id;

      if (userId) {
        // 🧨 Reset current anonymous user session
        posthog.reset();

        // 👤 Identify user with custom ID and properties
        posthog.identify(userId.toString(), {
          userId: userId || "",
        });
      }
    } catch (e) {
      console.error("Failed to parse userInfo from localStorage", e);
    }
    setFailedAttempts(0);
    localStorage.setItem("isFirstLogin", "true");
  };
  const handleResendOTP = async () => {
    if (resendCooldown > 0) {
      toast.info(
        t("toast.info.resendOTPCooldown", { seconds: resendCooldown })
      );
      return;
    }

    const body = { phone, country_code: countryCode };
    const apiCall = typeVerifyOTP === "signup" ? resendOTPSignUp : login;

    try {
      const response = await apiCall(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toast.success(t("toast.success.resendOTPSuccess"));
        setResendCooldown(300);
        setFailedAttempts(0);
        setIsBlocked(false);
      }
    } catch (err) {
      toast.error(t("toast.error.resendOTPFailed"));
      // console.error("Error:", err);
    }
  };

  useEffect(() => {
    let timer;
    if (blockTime > 0) {
      timer = setInterval(() => {
        setBlockTime((prev) => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            setIsBlocked(false);
            setFailedAttempts(0);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [blockTime]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  return (
    <div className="flex  flex-col justify-evenly px-11 w-full bg-white h-full  ">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <header className="flex  items-center w-full pt-[155px]">
        <img
          src={images.LogoLogin}
          className="w-[110px] h-[81.95px] aspect-[110/81.95]"
          alt=""
        />
      </header>
      <div className="flex flex-col gap-6 justify-between mt-[60px]  w-full bg-white ">
        <div className="flex flex-col items-center justify-center max-w-full w-[340px]">
          <div className="self-center w-full">
            <div className="w-full">
              <p className="text-2xl font-bold text-[#253FF6]">
                {t("whatAppOTP.enterWhatsAppOTP")}
              </p>
            </div>
          </div>

          <div className="flex flex-col w-full text-sm leading-none text-[#A0A0A0]">
            {isBlocked && (
              <div className="mb-4 w-full py-3 px-4 bg-red-50 border border-red-100 rounded-lg">
                <p className="text-red-600 font-medium text-center">
                  {t("whatAppOTP.accountTemporarilyBlocked")}
                </p>
                <p className="text-sm text-red-500 text-center mt-1">
                  {t("whatAppOTP.blockedDescription", {
                    time: blockTime,
                  })}
                </p>
              </div>
            )}
            <div className="mt-6">
              <OTPInput otp={otp} setOtp={setOtp} disabled={isBlocked} />
            </div>
            {failedAttempts > 0 && !isBlocked && (
              <p className="mt-2 text-amber-600 text-xs">
                {t("whatAppOTP.attemptsRemaining", {
                  attempts: 5 - failedAttempts,
                })}
              </p>
            )}

            <button
              onClick={handleSignIn}
              disabled={isBlocked}
              className={`overflow-hidden rounded-md gap-2.5 self-stretch px-4 py-5 mt-6 w-full text-lg font-semibold leading-none ${
                isBlocked
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "text-white bg-[#253FF6] "
              }  min-h-[54px]`}
            >
              {isBlocked ? t("whatAppOTP.pleaseWait") : t("base.login")}
            </button>

            <p className="self-start mt-6 w-full font-medium">
              <span
                className="text-[#A0A0A0]"
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 400,
                }}
              >
                {t("whatAppOTP.noRecieveOTP")}
              </span>{" "}
              <span
                onClick={handleResendOTP}
                className={`${
                  resendCooldown > 0
                    ? "text-[#A0A0A0]"
                    : "text-[#253FF6] cursor-pointer"
                }`}
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 700,
                }}
              >
                {resendCooldown > 0
                  ? `${t("whatAppOTP.resend")} (${resendCooldown}s)`
                  : t("whatAppOTP.resend")}
              </span>
            </p>
          </div>
        </div>
        {/* <Copyright /> */}
      </div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
}
